import { ReactNode } from 'react';
import { TFunction } from 'i18next';
import { ChevronRight, Loader2 } from 'lucide-react';

import { ActionButton } from '@/shared/ui/action-button';
import type { DataTableColumn } from '@/shared/ui/data-table';
import { cn } from '@/lib/utils';

import type { FlattenedRow } from './use-disposition-table';

export const createDispositionColumns = (
  t: TFunction,
  isDownloadingLabels?: boolean,
  onDownloadLabels?: (o?: number) => void
): DataTableColumn<FlattenedRow>[] => [
  {
    id: 'description',
    header: t('requestline.ownerItemDesc.label'),
    accessorKey: 'description',
    width: 'w-[250px]',
    cell: (row) => {
      if (row.level === 0) {
        return (
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-foreground">{row.description}</span>
            <ActionButton
              intent="document"
              size="xs"
              className="uppercase"
              disabled={isDownloadingLabels}
              onClick={(e) => {
                e.stopPropagation();
                onDownloadLabels?.(row.o);
              }}>
              {isDownloadingLabels && <Loader2 className="animate-spin" />}
              {t('cmrlist.shipping.tag')}
            </ActionButton>
          </div>
        );
      } else if (row.level === 1) {
        return <span className="text-xs font-medium text-blue-700">{row.description}</span>;
      } else {
        return (
          <div className="flex flex-col">
            <span className="text-[10px] text-muted-foreground">{row.date}</span>
            <span className="text-[11px] leading-tight text-foreground/80 italic">
              {row.description || <span className="text-muted-foreground/50">No description</span>}
            </span>
          </div>
        );
      }
    },
    className: (row: FlattenedRow) => {
      if (row.level === 1) return 'pl-10';
      if (row.level === 2) return 'pl-16';
      return '';
    }
  },
  {
    id: 'totalPerSrf',
    header: `${t('disp.col.linetotal.desc')} / ${t('requestline.ltuQty.short')}`,
    accessorKey: 'totalPerSrf',
    width: 'w-[90px]',
    cell: (row) => {
      if (row.level === 0 && row.totalPerSrf) {
        return (
          <div className="flex flex-col items-end">
            <span className="font-bold">{row.totalPerSrf.toLocaleString()}</span>
            <span className="text-[10px] text-muted-foreground uppercase">{row.unit}</span>
          </div>
        );
      }
      return null;
    },
    className: 'text-right',
    hideOnLevel: [1, 2]
  },
  {
    id: 'remainsWithSender',
    header: t('disp.col.remaining.desc'),
    accessorKey: 'remainsWithSender',
    width: 'w-[90px]',
    cell: (row) => row.remainsWithSender?.toLocaleString(),
    className: 'text-right text-orange-600 font-bold',
    hideOnLevel: [1, 2]
  },
  {
    id: 'cancelledBySender',
    header: t('disp.col.cancel.desc'),
    accessorKey: 'cancelledBySender',
    width: 'w-[90px]',
    cell: (row) => row.cancelledBySender?.toLocaleString(),
    className: 'text-right',
    hideOnLevel: [1, 2]
  },
  {
    id: 'receivedFromSender',
    header: t('disp.col.received.desc'),
    accessorKey: 'receivedFromSender',
    width: 'w-[90px]',
    cell: (row) => {
      if (row.level === 0) return row.receivedFromSender?.toLocaleString();
      if (row.level === 1) return row.received?.toLocaleString();
      if (row.level === 2 && row.quantity) return row.quantity.toLocaleString();
      return null;
    },
    className: (row: FlattenedRow) =>
      cn('text-right', row.level === 0 && 'font-bold', row.level === 2 && 'text-[11px] font-medium text-foreground/70')
  },
  {
    id: 'transferOut',
    header: t('disp.col.transferout.desc'),
    accessorKey: 'transferOut',
    width: 'w-[90px]',
    cell: (row) => row.transferOut?.toLocaleString(),
    className: 'text-right',
    hideOnLevel: [2]
  },
  {
    id: 'transferIn',
    header: t('disp.col.transferin.desc'),
    accessorKey: 'transferIn',
    width: 'w-[90px]',
    cell: (row) => row.transferIn?.toLocaleString(),
    className: 'text-right',
    hideOnLevel: [2]
  },
  {
    id: 'stockOnHand',
    header: t('disp.col.soh.desc'),
    accessorKey: 'stockOnHand',
    width: 'w-[90px]',
    cell: (row) => row.stockOnHand?.toLocaleString(),
    className: (row: FlattenedRow) =>
      cn(
        'text-right',
        row.level === 0 && 'text-green-600 font-bold',
        row.level === 1 && 'text-green-600',
        row.level === 2 && 'text-[11px] text-green-600/70'
      )
  },
  {
    id: 'reportedLost',
    header: t('disp.col.lost.desc'),
    accessorKey: 'reportedLost',
    width: 'w-[90px]',
    cell: (row) => row.reportedLost?.toLocaleString(),
    className: 'text-right',
    hideOnLevel: [2]
  },
  {
    id: 'releasedToReceiver',
    header: t('disp.col.released.desc'),
    accessorKey: 'releasedToReceiver',
    width: 'w-[90px]',
    cell: (row) => {
      if (row.level === 0) return row.releasedToReceiver?.toLocaleString();
      if (row.level === 1) return row.released?.toLocaleString();
      return null;
    },
    className: (row: FlattenedRow) =>
      cn('text-right', row.level === 0 && 'font-bold', row.level === 1 && 'font-medium'),
    hideOnLevel: [2]
  }
];

export const renderExpandIcon = (row: FlattenedRow, isExpanded: boolean): ReactNode => {
  if (!row.children || row.children.length === 0) return null;

  return (
    <ChevronRight
      className={cn(
        'transition-transform duration-200',
        isExpanded && 'rotate-90',
        row.level === 0 && 'w-4 h-4 text-muted-foreground',
        row.level === 1 && 'w-3 h-3 text-blue-400'
      )}
    />
  );
};
